'use strict';
namespace TR {

    import BoardType = Core.Hotels.BoardType;

    interface SeoLink {
        text:  string;
        href: string;
    }

    interface LastMinuteDealsConfig {
        findOptionsPageSize: number;
        findOptionsFlexibility: number;
        smallBatchTake: number;
        halfBatchTake: number;
        halfBatchChunkSize: number;
    }

    interface LastMinuteDealsDestination {
        TypeId: string;
        Type: number;
        Id: number;
        Title: string;
        deals?: any;
    }

    interface FlightsDeal {
        AdultFare: number;
        AdultTax: number;
        CurrencyCode: string;
        DestinationAirport: string;
        DestinationAirportTitle: string;
        FlightId: string;
        InboundArrivalDate: string;
        InboundDepartureDate: string;
        InboundDuration: number;
        InboundStops: number;
        OriginAirport: string;
        OriginAirportTitle: string;
        OutboundArrivalDate: string;
        OutboundDepartureDate: string;
        OutboundDuration: number;
        OutboundStops: number;
        Price: number;
        ReturnArrivalAirport: string;
        ReturnArrivalAirportTitle: string;
        SearchId: string;
        SearchIds: string[];
    }

    interface BoardTypeMinPrices {
        BoardType: BoardType;
        MinPrice: number;
        WasPrice: number;
    }

    interface HotelDeal {
        AvailabilitySearchId: string;
        BoardType: BoardType;
        BoardTypeMinPrices: BoardTypeMinPrices[];
        CheckInDate: string;
        CheckOutDate: string;
        NonRefundable: boolean;
        PayAtHotelAvailable: boolean;
        Price: number;
        RefundableCutOffDate: string;
        WasPrice: number;
    }

    interface HolidayDeals {
        FlightsDeal: FlightsDeal;
        HotelDeal: HotelDeal;
    }

    interface LinkedAirports {
        Code: string;
        DistanceKm: number;
        Latitude: number;
        Longitude: number;
        ShortTitle: string;
        Title: string;
    }

    interface TransferTimes {
        Airport: string;
        Minutes: number;
    }

    interface Weather {
        Month: number;
        Temperature: number;
    }

    interface Deal {
        AcomType: string;
        AcomTypeId: number;
        Address: string;
        Airports: string[];
        BoardType: string;
        CallToBookBehaviour: number;
        Country: string;
        CountryId: number;
        CroppedImage: string;
        CurrentViews: number;
        CustomerRating: number;
        CustomerRatingCount: number;
        CustomerRatingTitle: string;
        DealMinPrice: number;
        DealWasPrice: number;
        Description: string;
        DiscountPercentage: number;
        DisplayIndex: number;
        Duration: number;
        HolidayDeals: HolidayDeals[];
        Id: number;
        ImageUrl: string;
        IndicativeMinPrice: number;
        IsVilla: boolean;
        Latitude: number;
        LinkedAirports: LinkedAirports[];
        Location: string;
        LocationId: number;
        Longitude: number;
        MinCost: number;
        MinPricePerson: number;
        NonRefundable: boolean;
        NumberOfPhotos: number;
        OriginalCost: number;
        PayAtHotelAvailable: boolean;
        Postcode: string;
        PropertyTypeFrom: string;
        Province: string;
        ProvinceId: number;
        Recommended: boolean;
        RoomNightsBooked: number;
        ShortTitle: string;
        StarRating: number;
        StarRatingTitle: string;
        StarRatingV2: number;
        Summary: string;
        Title: string;
        TransferTimes: TransferTimes[];
        Url: string;
        WasPricePerPerson: number;
        Weather: Weather[];
    }

    interface DealChunks {
        SmallBatch: Deal[][];
        FirstHalfBatch: Deal[][];
        SecondHalfBatch: Deal[][];
    }

    let startDateInitialised = TRPCONFIG.Domain.DeviceType !== 'Mobile';

    class LastMinuteDeals {

        summary: any;
        blocks: any;
        destinations: LastMinuteDealsDestination[];
        focusedDestination: LastMinuteDealsDestination;
        focusedSpecialDeals: boolean;
        destinationsWithDeals: LastMinuteDealsDestination[];
        unblockOnce: ()=>void;
        seoLinks: SeoLink[];
        showMore: boolean;
        isLoading: boolean = true;
        hasDeals: boolean = false;
        hasSpecialDeals: boolean = false;
        counter = 0;
        config: LastMinuteDealsConfig;
        priceMode = 0;
        showDiscount: boolean = false;
        specialDeals: DealChunks | undefined;

        constructor(
            private $q: ng.IQService,
            private $location: ng.ILocationService,
            private $timeout: ng.ITimeoutService,
            private $scope: any,
            private $rootScope: any,
            private $route: any,
            private tripService: any,
            private filtering: any,
            private pageStrand: any,
            private analytics: any,
            private broker: Core.Broker,
            private holidayDeals: any,
            private holidayDealsGrouped: TR.HolidayDealsGrouped,
            private holidayDealsPromoService: any,
            private hotelFromSession: any,
            private dateUtil: any,
            private dealsBoxService: any,
            private occupancyService: Core.OccupancyService,
            private searchUnitService: Search.SearchUnitService,
            private scrollService: any,
            private funcUtil: any,
            private resourceService: Core.ResourceService,
            private destinationService: any
        ) {
            $scope.navigatorUserAgent = '|' + navigator.userAgent + '|' + /prerender/i.test(navigator.userAgent) + '|';
            this.watchForPageRedirection();

            if (!startDateInitialised) {
                let startDate = this.dateUtil.getNextSaturday(TRPCONFIG.Domain.HolidayDealsDefaultWeeks);
                searchUnitService.startDate(1, startDate);
                startDateInitialised = true;
            }

            this.config = ((): LastMinuteDealsConfig => {
                let configs = {
                    Default: {
                        findOptionsPageSize: 32,
                        smallBatchTake: 12,
                        halfBatchTake: 4,
                        halfBatchChunkSize: 4,
                        slidesToScroll: 4,
                        slidesToShow: 4
                    },
                    Phone: {
                        findOptionsPageSize: 16,
                        smallBatchTake: 4,
                        halfBatchTake: 8,
                        halfBatchChunkSize: 1,
                        slidesToScroll: 1,
                        slidesToShow: 1
                    },
                    Tablet: {
                        findOptionsPageSize: 24,
                        smallBatchTake: 6,
                        halfBatchChunkSize: 3
                    }
                };
                _.merge(configs, TRPCONFIG.Domain.LastMinuteDealsConfig);
                return _.merge(configs.Default, configs[TRPCONFIG.DeviceCategory]);
            })();

            this.broker.subscribe('lastMinuteDealsCtrl', 'priceModeChanged',
                eventData => this.priceMode = eventData.data);
            this.$scope.$on('$destroy', () => this.broker.unsubscribe('lastMinuteDealsCtrl'));

            $scope.$emit('block', {
                title: resourceService.get('SEARCHINGDEALS'),
                subtitle: resourceService.get('THISMAYTAKE'),
                loadingScreenStyle: 'default-lastMinuteDeals'
            });
            this.unblockOnce = _.once(() => this.$scope.$emit('unblock'));

            this.$rootScope.hideFooterToolbar = true;
            this.prepopulateForm();

            this.$q.when(TRPCONFIG.Domain.LastMinuteDealsDestinations)
                .then(config => this.destinations =
                    _(config.split('|'))
                        .map(d => d.split('-'))
                        .map(([ Type, Id, Title ]) => ({
                            Title,
                            Type: parseInt(Type, 10),
                            Id: parseInt(Id, 10),
                            TypeId: `${Type}-${Id}`,
                        }))
                        .value()
                )
                .then(() => {
                    let foundDestination;
                    const searchDestination: string = _.get<string>(this.$location.search(), 'destination');
                    const specialDeals: string = _.get<string>(this.$location.search(), 'specialDeals');

                    if (searchDestination) {
                        foundDestination = _.find(this.destinations,
                            (d: LastMinuteDealsDestination) => d.TypeId === searchDestination);
                        if (foundDestination) {
                            this.setFocusedDestination(foundDestination);
                        }
                    }
                    const LandingDestination: string = specialDeals ?
                        '45Star' :
                        foundDestination ?
                            foundDestination.Title :
                            'all';
                    this.analytics.track('mixpanel', 'WasNowLanding', { LandingDestination });
                })
                .then(() => {
                    if (TRPCONFIG.Domain.DeviceType === 'Mobile') {
                        this.$scope.$watch(
                            () => angular.toJson({
                                StartDate: this.searchUnitService.startDate(1),
                                Duration: this.searchUnitService.duration(1),
                                Flexibility: this.searchUnitService.dealsFlexibility,
                            }),
                            () => this.loadAllDeals()
                        );
                    }
                    this.$scope.$on('$routeUpdate', () => this.loadAllDeals());
                    this.loadAllDeals();
                });
            this.seoLinks = this.parseSeoLinks();

            this.$scope.$watch(() => this.$location.search().destination, value => {
                if (value === undefined) {
                    this.setFocusedDestination(undefined);
                }
            });

            this.$scope.$watch(() => this.$location.search().specialDeals, value => this.setFocusedSpecialDeals(value));

            /* UI-4273: Deals - % Discount Merchandising - A/B Test */
            this.broker.subscribe('LastMinuteDeals', 'HolidayDealDiscountPercentage', () => {
                this.$scope.$apply(() => {
                    this.showDiscount = true;
                });
            });
        }

        activeDestinations() {
            return this.focusedDestination ? [this.focusedDestination] : this.destinations;
        }

        loadAllDeals() {
            const specialDealStarLimit: number = 4;
            let counter = ++this.counter;
            let activeDestinations = this.activeDestinations();
            _.each(this.destinations, d => delete d.deals);

            this.$timeout(() => {
                this.isLoading = true;
                this.hasDeals = false;
                this.hasSpecialDeals = false;

                this.funcUtil
                    .waitForValue(() => this.tryGetOptions())
                    .then(options => this.holidayDealsGrouped.find(options))
                    .then(response => {

                        if (counter === this.counter) {
                            this.destinationsWithDeals = [];
                            let specialDealsFilter: Deal[][] = [];
                            _.each(response, (deals, key) => {
                                specialDealsFilter.push(
                                    _.filter(deals, (deal: Deal) => deal.StarRatingV2 >= specialDealStarLimit)
                                );
                                let typeId = (key + '').replace('|', '-');
                                this.destinationsWithDeals.push(_.find(this.destinations, d => d.TypeId === typeId));
                                let match = _.find(activeDestinations, d => d.TypeId === typeId);
                                if (match && deals && deals.length) {
                                    this.hasDeals = true;
                                    const pageStrand = this.pageStrand.fromRoute();

                                    if(!TRPCONFIG.Domain.CultureCode.includes('en')) {
                                        this.destinationService
                                        .getDestinationInfo(match.Id, match.Type, pageStrand)
                                        .then(destinationDetail => {
                                            if (destinationDetail){
                                                  match.Title = destinationDetail.Title;
                                            }
                                        });
                                    }

                                    match.deals = this.mapDealsToChunks(deals);
                                }
                            });
                            this.specialDeals = this.makeDealChunksFromDeals(
                                _.slice(
                                    _.sortBy(
                                        _.flatten(specialDealsFilter), 'MinPricePerson'
                                    ), 0, this.config.findOptionsPageSize
                                )
                            );
                            this.hasSpecialDeals = !!this.specialDeals;
                            this.isLoading = false;

                            this.unblockOnce();
                        }
                    });
            });
        }

        tryGetOptions() {
            let options;
            if (TRPCONFIG.Domain.DeviceType === 'Mobile') {
                options = {
                    startDate: this.dateUtil.parseDate(this.searchUnitService.startDate(1)),
                    duration: this.searchUnitService.duration(1),
                    occupancy: this.occupancyService.getOccupancy(),
                    depAir: this.searchUnitService.getDepartureAirports(),
                    flexibility: this.searchUnitService.dealsFlexibility,
                };
            } else {
                let params = this.holidayDeals.deserialiseParameters();
                options = {
                    startDate: params.startDate,
                    duration: params.duration,
                    occupancy: params.occupancy,
                    depAir: params.depAir,
                };
            }

            if (!options.startDate || !options.duration
                 || !options.occupancy || !options.occupancy[0]
                 || !options.depAir || !options.depAir[0]) {
                 return undefined;
            }

            options.destinations = this.destinations;
            options.dealsPerDestination = this.config.findOptionsPageSize;
            return options;
        }

        makeDealChunksFromDeals(deals: Deal[]): DealChunks | undefined {
            if (deals && deals[0]) {
                let chunks = _.chunk(deals, this.config.halfBatchChunkSize);
                return {
                    SmallBatch: [_.take(deals, this.config.smallBatchTake)],
                    FirstHalfBatch: _.take(chunks, this.config.halfBatchTake),
                    SecondHalfBatch: _.slice(chunks, this.config.halfBatchTake)
                };
            }
        }

        mapDealsToChunks(deals: Deal[]): DealChunks | undefined {
            if (deals && deals[0]) {
                _.forEach(deals, (deal: Deal, index: number) => {
                    deal.ImageUrl = deal.CroppedImage;
                    deal.DisplayIndex = index;
                });
                return this.makeDealChunksFromDeals(deals);
            }
        }

        dropdownDestinations() {
            let focusedTypeId = _.get(this, 'focusedDestination.TypeId');
            return _.filter(this.destinations, d =>
                d.TypeId === focusedTypeId || _.find(this.destinationsWithDeals, x => x === d));
        }

        prepopulateForm() {
            let startDate = this.searchUnitService.startDate(1);
            let minDate = this.dateUtil.todayPlus(3);
            if (!startDate || startDate < minDate) {
                this.searchUnitService.startDate(1, minDate);
            }

            let duration = this.searchUnitService.duration(1);
            /* tslint:disable */
            let isValid = _.some(TRPCONFIG.Domain.HolidayDealsDurations, d => d == duration);
            /* tslint:enable */
            if (!isValid) {
                this.searchUnitService.duration(1, TRPCONFIG.Domain.HolidayDealsDefaultDuration);
            }
        }

        setFocusedDestination(destination?: LastMinuteDealsDestination) {
            if (destination) {
                this.showMore = false;
                this.focusedDestination = destination;
                this.focusedSpecialDeals = false;
                this.$location
                    .search('destination', destination.TypeId)
                    .search('specialDeals', null)
                    .replace();
            } else {
                delete this.focusedDestination;
                this.$location
                    .search('destination', null)
                    .replace();
            }
            this.scrollService.scrollTo('top');
        }

        setFocusedSpecialDeals(specialDeals: boolean) {
            if (specialDeals) {
                this.showMore = false;
                this.$location
                    .search('specialDeals', 1)
                    .search('destination', null)
                    .replace();
            } else {
                this.$location
                    .search('specialDeals', null)
                    .replace();
            }
            this.focusedSpecialDeals = specialDeals;
            this.scrollService.scrollTo('top');
        }

        goToDeal(deal: Deal, DealCategory: string): void {
            this.analytics.track('mixpanel', 'WasNowCardClick', {
                CardDestination: `${deal.Country}, ${deal.Province}`,
                CardRank: deal.DisplayIndex + 1,
                EstabId: deal.Id,
                OverlayShown: this.showDiscount,
                OverlayValue: `${deal.DiscountPercentage}%`,
                DealCategory
            });

            let occupancy = this.occupancyService.parseOccupancyString(this.$location.search().occ);
            let selected = deal.HolidayDeals[0];
            let estabId = deal.Id;

            this.$scope.$emit('block', {
                title: this.resourceService.get('LOADINGHOTELS'),
                subtitle: this.resourceService.get('THISMAYTAKE'),
                loadingScreenStyle: 'default-lastMinuteDeals-select'
            });

            this.tripService
                .getTripIdForHolidayDeal(selected, occupancy)
                .then(tripId => this.$location.path(`/holidays/${tripId}/${estabId}`));
        }

        showMoreClick(ShowMoreDestination: string) {
            this.showMore = true;
            this.analytics.track('mixpanel', 'WasNowShowMore', { ShowMoreDestination });
        }

        watchForPageRedirection() {
            //This function protects against a bug which happens when clicking on a link in the menu.
            //e.g. <a href="holidaydeals?destination=3-54859">
            //When this happens the page must reload and start fresh.
            let wasPopulated = false;
            this.$scope.$watch(() => Object.keys(this.$location.search()).length > 2, isPopulated => {
                if (wasPopulated && !isPopulated) {
                    this.$route.reload();
                }
                wasPopulated = wasPopulated || isPopulated;
            });
        }

        parseSeoLinks() {
            let seoLinks = `Last Minute,holidays/last-minute|
City Breaks,holidays/city-breaks|Adults Only,holidays/adults|
Self Catering,holidays/self-catering|Spa Breaks,spa-breaks|Family,holidays/family|Couples,holidays/couples|
Romantic Breaks,romantic-breaks|Ski,holidays/ski|Beach,holidays/beach|Winter Sun,holidays/winter-sun|
Summer,holidays/summer|Waterparks,holidays/family/waterparks`
                .replace(/\r?\n/g, '')
                .split('|');
            return _(seoLinks)
                .map(x => x.split(','))
                .map(x => ({text: x[0], href: x[1]}))
                .value();
        }
    }

    angular
        .module('TR')
        .controller('lastMinuteDeals', LastMinuteDeals);
}
